import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './context/LanguageContext';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { LoadingSpinner } from './components/LoadingSpinner';
import { Analytics } from './components/Analytics';
import TawkChat from './components/TawkChat';
import PresupuestoProgramable from './pages/modules/PresupuestoProgramable';
import PlaneacionControl from './pages/modules/PlaneacionControl';
import AdministracionCompras from './pages/modules/AdministracionCompras';
import Dependencias from './pages/modules/Dependencias';
import CuantificacionBIM from './pages/modules/CuantificacionBIM';
import ErpEnterprise from './pages/modules/ErpEnterprise';
import Distribuidores from './pages/Distribuidores';
import DistribuidorDetalle from './pages/DistribuidorDetalle';
import ErpVentas from './pages/modules/ErpVentas';
import ErpOperaciones from './pages/modules/ErpOperaciones';
import ErpProductividad from './pages/modules/ErpProductividad';
import ErpWeb from './pages/modules/ErpWeb';
import Inforte from './pages/Inforte';
import FAQ from './pages/FAQ';
import Downloads from './pages/Downloads';
import DownloadsModule1 from './pages/DownloadsModule1';
import DownloadsModule2 from './pages/DownloadsModule2';
import DownloadsModule3 from './pages/DownloadsModule3';
import DownloadsModule5 from './pages/DownloadsModule5';
import DownloadsOpus24Module1 from './pages/DownloadsOpus24Module1';
import DownloadsOpus24Module2 from './pages/DownloadsOpus24Module2';
import Changelog24Module1 from './pages/Changelog24Module1';
import Changelog24Module2 from './pages/Changelog24Module2';
import AcademiaOpus from './pages/AcademiaOpus';
import Certificaciones from './pages/Certificaciones';
import ProductosOpus from './pages/ProductosOpus';
import ProductosOpusErp from './pages/ProductosOpusErp';
import Anywhere from './pages/Anywhere';
import ContactInfo from './pages/ContactInfo';
import EnConstruccion from './pages/EnConstruccion';

// Lazy-loaded components
const Home = lazy(() => import('./pages/Home'));
const Services = lazy(() => import('./pages/Services'));
const Projects = lazy(() => import('./pages/Projects'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));

// ScrollToTop component to reset scroll position on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    // Force scroll to top in multiple ways to ensure it works
    window.scrollTo(0, 0);
    
    // Also try to do it after a small delay 
    // (sometimes needed to let the page render first)
    setTimeout(() => {
      window.scrollTo(0, 0);
      
      // If we have access to document body, also try to reset its scroll
      if (document.body) {
        document.body.scrollTop = 0;
      }
      
      // If we have access to document documentElement (html), also reset its scroll
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
      }
    }, 0);
  }, [pathname]);
  
  return null;
};

export default function App() {
  return (
    <LanguageProvider>
      <HelmetProvider>
        <div className="flex flex-col min-h-screen">
          <Analytics />
          <ScrollToTop />
          <Header />
          <main className="flex-grow">
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/servicios" element={<Services />} />
                <Route path="/proyectos" element={<Projects />} />
                <Route path="/proyectos/:id" element={<ProjectDetail />} />
                <Route path="/nosotros" element={<About />} />
                <Route path="/contacto" element={<ContactInfo />} />
                <Route path="/soporte/solicitud" element={<Contact />} />
                <Route path="/soporte/faq" element={<FAQ />} />
                <Route path="/soporte/descargas" element={<Downloads />} />
                <Route path="/soporte/descargas/opus22/m1" element={<DownloadsModule1 />} />
                <Route path="/soporte/descargas/opus22/m2" element={<DownloadsModule2 />} />
                <Route path="/soporte/descargas/opus22/m3" element={<DownloadsModule3 />} />
                <Route path="/soporte/descargas/opus22/m5" element={<DownloadsModule5 />} />
                <Route path="/soporte/descargas/opus24/m1" element={<DownloadsOpus24Module1 />} />
                <Route path="/soporte/descargas/opus24/m2" element={<DownloadsOpus24Module2 />} />
                <Route path="/soporte/descargas/opus24/m1/changelog" element={<Changelog24Module1 />} />
                <Route path="/soporte/descargas/opus24/m2/changelog" element={<Changelog24Module2 />} />
                <Route path="/soporte/anywhere" element={<Anywhere />} />
                <Route path="/academia-opus" element={<AcademiaOpus />} />
                <Route path="/certificaciones" element={<Certificaciones />} />
                <Route path="/productos/opus" element={<ProductosOpus />} />
                <Route path="/productos/opus-erp" element={<ProductosOpusErp />} />
                <Route path="/modulos/presupuesto-programable" element={<PresupuestoProgramable />} />
                <Route path="/modulos/planeacion-control" element={<PlaneacionControl />} />
                <Route path="/modulos/administracion-compras" element={<AdministracionCompras />} />
                <Route path="/modulos/dependencias" element={<Dependencias />} />
                <Route path="/modulos/cuantificacion-bim" element={<CuantificacionBIM />} />
                <Route path="/modulos/erp-enterprise" element={<ErpEnterprise />} />
                <Route path="/distribuidores" element={<Distribuidores />} />
                <Route path="/distribuidores/:id" element={<DistribuidorDetalle />} />
                <Route path="/erp/ventas" element={<ErpVentas />} />
                <Route path="/erp/operaciones" element={<ErpOperaciones />} />
                <Route path="/erp/productividad" element={<ErpProductividad />} />
                <Route path="/erp/web" element={<ErpWeb />} />
                <Route path="/inforte" element={<Inforte />} />
                <Route path="/en-construccion" element={<EnConstruccion />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
          <TawkChat />
        </div>
      </HelmetProvider>
    </LanguageProvider>
  );
} 